import { cn, nameOrPhone } from "@/lib/utils"

/**
 * The people behind a number, scrollable.
 *
 * Lives on its own because two dashboards ask the same question of a bar — the
 * onboarding funnel ("who stopped here") and Feature Engagement ("who actually
 * used this") — and a count you cannot turn into a call list is a count nobody
 * acts on.
 */
export function NameList({
  title,
  names,
  tone,
  limit,
}: {
  title: string
  names: string[]
  tone: "good" | "bad"
  /**
   * Cap the list instead of scrolling it, for somewhere a scrollbar can't be
   * used — a hover preview is `pointer-events-none`, so its scrollbar is
   * decoration and the names under the fold are simply unreachable. Capped
   * means the count stays honest and the remainder is stated out loud.
   */
  limit?: number
}) {
  if (!names.length) return null
  const shown = limit ? names.slice(0, limit) : names
  return (
    <div className="min-w-0 flex-1">
      <div
        className={cn(
          "text-sm font-semibold uppercase tracking-wide mb-1",
          tone === "good" ? "text-emerald-600" : "text-red-600",
        )}
      >
        {title} · {names.length}
      </div>
      <ul className={cn("pr-1 space-y-0.5", !limit && "max-h-52 overflow-y-auto")}>
        {/* Index in the key: two members can share a name, and everyone we
         *  can't name arrives as the same placeholder string — keying on the
         *  name alone collapses them, so the list would show fewer people
         *  than the count above it. */}
        {shown.map((n, i) => (
          <li key={`${n}-${i}`} className="text-sm leading-tight truncate">
            {nameOrPhone(n)}
          </li>
        ))}
      </ul>
      {shown.length < names.length && (
        <div className="text-sm text-muted-foreground mt-1">
          +{names.length - shown.length} more
        </div>
      )}
    </div>
  )
}
